import { Link } from 'react-router-dom';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { ApiError } from '../lib/api';

/* ------------------------------------------------------------------ toasts */

type Tone = 'ok' | 'bad' | '';
interface Toast { id: number; message: string; tone: Tone; }

const ToastCtx = createContext<(message: string, tone?: Tone) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((message: string, tone: Tone = '') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 6000);
  }, []);

  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone}`}>{t.message}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

/* ------------------------------------------------------------------ data */

export interface Resource<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/** Minimal fetch-on-mount hook. Keeps stale data visible while reloading. */
export function useResource<T>(loader: () => Promise<T>, deps: unknown[] = []): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loader()
      .then((value) => {
        if (cancelled) return;
        setData(value);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload: () => setNonce((n) => n + 1) };
}

/* ------------------------------------------------------------------ layout */

export function PageHead({
  eyebrow, backTo, title, description, children,
}: { eyebrow?: string; backTo?: string; title: string; description?: string; children?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        {eyebrow && (backTo
          ? <Link className="eyebrow-mono eyebrow-back" to={backTo}>← {eyebrow}</Link>
          : <div className="eyebrow-mono">{eyebrow}</div>)}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {children && <div className="actions">{children}</div>}
    </div>
  );
}

export function Card({ title, children, style }: { title?: string; children: ReactNode; style?: React.CSSProperties }) {
  return (
    <div className="card" style={style}>
      {title && <h3>{title}</h3>}
      {children}
    </div>
  );
}

export function Stat({
  label, value, sub, tone,
}: { label: string; value: ReactNode; sub?: string; tone?: 'ok' | 'bad' | 'warn' }) {
  return (
    <div className="card stat">
      <h3>{label}</h3>
      <div className={`value ${tone ?? ''}`}>{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

export function Pill({ tone, tag, children }: { tone?: 'ok' | 'warn' | 'bad' | 'brand'; tag?: boolean; children: ReactNode }) {
  return <span className={`pill ${tone ?? ''}${tag ? ' tag' : ''}`}>{children}</span>;
}

export function Hash({ value, head = 8 }: { value?: string | null; head?: number }) {
  if (!value) return <span className="muted">—</span>;
  return (
    <span className="hash" title={value}>
      {value.length > head + 4 ? `${value.slice(0, head)}…${value.slice(-2)}` : value}
    </span>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Loading({ what = 'Loading' }: { what?: string }) {
  return <div className="empty">{what}…</div>;
}

export function ErrorNote({ error }: { error: string | null }) {
  if (!error) return null;
  return <div className="note bad" role="alert">{error}</div>;
}

export function Field({
  label, help, children,
}: { label: string; help?: string; children: ReactNode }) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {help && <div className="help">{help}</div>}
    </div>
  );
}

export function Modal({
  title, description, children, onClose, footer, wide,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>
        {description && <p className="muted small" style={{ marginTop: 0 }}>{description}</p>}
        {children}
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

/** Copy-once secret display — API keys and webhook secrets are shown one time. */
export function SecretReveal({ label, secret, note }: { label: string; secret: string; note?: string }) {
  const toast = useToast();
  return (
    <div className="note warn" style={{ marginTop: 12 }}>
      <strong>{label}</strong>
      <div className="mono" style={{ margin: '8px 0', wordBreak: 'break-all' }}>{secret}</div>
      <button
        className="small"
        onClick={() => {
          navigator.clipboard.writeText(secret).then(
            () => toast('Copied to clipboard', 'ok'),
            () => toast('Copy failed.', 'bad'),
          );
        }}
      >
        Copy
      </button>
      {note && <div style={{ marginTop: 8 }}>{note}</div>}
    </div>
  );
}
